import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { db } from '../../../../../firebase/config';
import { doc, getDoc, onSnapshot, updateDoc } from 'firebase/firestore';

/**
 * useDetalleAumentoArt - Hook de negocio unificado para el detalle de aumentos de ART.
 * Soportado de forma polimórfica para ruteo completo y renderizado inline mediante propiedades.
 * Autocorregido con resolución de paths dinámica para prevenir fallos por desalineación de índices.
 */
export const useDetalleAumentoArt = (initialCotizacion, onBack) => {
    const location = useLocation();
    const navigate = useNavigate();
    const stateData = location.state || {};

    const isFullUser = true;

    // --- RESOLUCIÓN POLIMÓRFICA Y AUTOREPARABLE DE ESTADOS ---
    const state = useMemo(() => {
        if (initialCotizacion) {
            const pathParts = initialCotizacion.path ? initialCotizacion.path.split('/') : [];
            
            // Localizamos de forma dinámica el identificador de la entidad
            const clientIndex = pathParts.findIndex(p => p === 'clientes' || p === 'prospectos');
            const resolvedClienteId = clientIndex !== -1 
                ? pathParts[clientIndex + 1] 
                : (initialCotizacion.clienteId || initialCotizacion.entidadId);

            // Localizamos de forma dinámica el identificador de la cotización
            const cotIndex = pathParts.indexOf('cotizaciones_art');
            const resolvedCotizacionId = cotIndex !== -1 
                ? pathParts[cotIndex + 1] 
                : initialCotizacion.id;

            return {
                cotizacionId: resolvedCotizacionId,
                clienteId: resolvedClienteId,
                clientId: resolvedClienteId,
                contratoId: initialCotizacion.contratoOrigenId || initialCotizacion.contratoVinculadoId || initialCotizacion.contratoId,
                endosoId: initialCotizacion.endosoVinculadoId || initialCotizacion.endosoId,
                path: initialCotizacion.path
            };
        }
        return stateData;
    }, [initialCotizacion, stateData]);

    // --- ESTADOS DE CONTROL DE DATOS ---
    const [loading, setLoading] = useState(true);
    const [cotizacion, setCotizacion] = useState(initialCotizacion || null);
    const [fetchedClient, setFetchedClient] = useState(null);
    const [endosoFull, setEndosoFull] = useState(null); 
    const [contractFull, setContractFull] = useState(null);
    const [isGenerating, setIsGenerating] = useState(false);
    
    // --- ESTADOS DE MODALES ---
    const [showContratoModal, setShowContratoModal] = useState(false);
    const [showAnulacionModal, setShowAnulacionModal] = useState(false);
    const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });

    const [contratoPrefill, setContratoPrefill] = useState(null);
    const [clientDataForModal, setClientDataForModal] = useState(null);
    const [selectedComparacion, setSelectedComparacion] = useState(null);
    const [tempContractData, setTempContractData] = useState(null);

    const captureRef = useRef(null);

    // --- 1. SUSCRIPCIÓN EN TIEMPO REAL Y CARGA EN CASCADA ---
    useEffect(() => {
        let unsubscribeCot = () => {};
        
        const initDataFetch = async () => {
            const { cotizacionId, entidadId, clientId, contratoId, endosoId, path } = state;
            const finalClienteId = clientId || entidadId; 

            if (!finalClienteId || !cotizacionId) {
                setLoading(false);
                return;
            }

            try {
                // Buscamos de forma directa usando el path físico absoluto de Firestore si existe
                const cotRef = path 
                    ? doc(db, path) 
                    : doc(db, `public/data/clientes/${finalClienteId}/cotizaciones_art`, cotizacionId);
                
                unsubscribeCot = onSnapshot(cotRef, async (snap) => {
                    if (snap.exists()) {
                        const cData = { id: snap.id, path: snap.ref.path, ...snap.data() };
                        setCotizacion(cData);
                        
                        const parentContratoId = contratoId || cData.contratoOrigenId || cData.contratoVinculadoId;
                        const finalEndosoId = endosoId || cData.endosoVinculadoId;

                        if (parentContratoId && finalEndosoId) {
                            const endDocRef = doc(db, `public/data/clientes/${finalClienteId}/contratos/${parentContratoId}/aumentos_art`, finalEndosoId);
                            const endosoSnap = await getDoc(endDocRef);
                            if (endosoSnap.exists()) {
                                setEndosoFull({ id: endosoSnap.id, path: endosoSnap.ref.path, ...endosoSnap.data() });
                            }
                            
                            const conSnap = await getDoc(doc(db, `public/data/clientes/${finalClienteId}/contratos`, parentContratoId));
                            if (conSnap.exists()) {
                                setContractFull({ id: conSnap.id, ...conSnap.data() });
                            }
                        }
                    } else {
                        setCotizacion(null);
                    }
                    setLoading(false);
                }, (err) => {
                    console.error("Error en Snapshot de Cotización de aumento:", err);
                    setCotizacion(null);
                    setLoading(false);
                });

                const cliDoc = await getDoc(doc(db, 'public/data/clientes', finalClienteId));
                if (cliDoc.exists()) {
                    setFetchedClient({ id: cliDoc.id, ...cliDoc.data() });
                }

            } catch (err) {
                console.error("Error cargando detalle de aumento de ART:", err);
                setLoading(false);
            }
        };

        initDataFetch();
        return () => unsubscribeCot();
    }, [state]);

    // --- 2. DETECTOR DE PARPADEO (needsHydration) ---
    const needsHydration = useMemo(() => {
        const { cotizacionId, entidadId, clientId, contratoId, endosoId } = state;
        const finalClienteId = clientId || entidadId;

        if (!finalClienteId || !cotizacionId) return false;
        if (!cotizacion) return false; // Evitamos loading infinito si la cotización no existe
        
        const hasQuote = !!cotizacion;
        const hasClient = !!fetchedClient;
        const hasEndoso = endosoId ? !!endosoFull : true;
        const hasContract = contratoId ? !!contractFull : true;

        return !hasQuote || !hasClient || !hasEndoso || !hasContract;
    }, [cotizacion, fetchedClient, endosoFull, contractFull, state]);

    const combinedLoading = useMemo(() => {
        return loading || needsHydration;
    }, [loading, needsHydration]);

    // --- 3. ACCIONES TRANSACCIONALES ---
    const handleOpenContractModal = useCallback(() => {
        if (!cotizacion || !!cotizacion.nContratoVinculado) return;
        const o = selectedComparacion || (cotizacion.cotizaciones?.filter(c => parseFloat(c.alicuotaCotizada) > 0)[0] || null);
        
        setContratoPrefill({
            ciiu: cotizacion.ciiu || contractFull?.ciiu || fetchedClient?.ciiu || '',
            capitas: cotizacion.capitas || '',
            masaSalarial: cotizacion.masaSalarial || '',
            alicuota: o?.alicuotaCotizada || '', 
            compania: o?.compania || '', 
            vendedor: cotizacion.vendedor || fetchedClient?.vendedor || '',
            canal: cotizacion.canal || fetchedClient?.canal || fetchedClient?.tipoCanal || '',
            canalNombreId: cotizacion.canalNombreId || fetchedClient?.canalNombreId || '',
            canalNombre: cotizacion.canalNombre || fetchedClient?.canalNombre || '',
            subcanalNombreId: cotizacion.subcanalNombreId || fetchedClient?.subcanalNombreId || '',
            subcanalNombre: cotizacion.subcanalNombre || fetchedClient?.subcanalNombre || '',
            tipoOperacion: 'Emisión por Reemplazo',
            cotizacionId: cotizacion.id, 
            cotizacionPath: cotizacion.path 
        });
        setClientDataForModal({ ...fetchedClient, id: cotizacion.clienteId || fetchedClient?.id });
        setShowContratoModal(true);
    }, [cotizacion, contractFull, fetchedClient, selectedComparacion]);

    const handleContractSuccess = useCallback((msg, contractData) => {
        setShowContratoModal(false);
        setTempContractData(contractData);
        setTimeout(() => setShowAnulacionModal(true), 300);
    }, []);

    const handleAnulacionSuccess = useCallback(async () => {
        setShowAnulacionModal(false);
        const { cotizacionId, clientId, entidadId } = state;
        const finalClientId = clientId || entidadId || cotizacion?.clienteId;

        if (!finalClientId || !cotizacionId || !tempContractData) return;

        try {
            const cotRef = doc(db, `public/data/clientes/${finalClientId}/cotizaciones_art`, cotizacionId);
            const vinculacion = {
                estado: 'Cerrada Ganada',
                nContratoVinculado: tempContractData.nContratoVinculado || tempContractData.nContrato || 'S/N',
                contratoVinculadoId: tempContractData.contratoVinculadoId || tempContractData.id || '',
                ultimaModificacion: new Date().toISOString()
            };
            
            await updateDoc(cotRef, vinculacion);
            setCotizacion(prev => ({ ...prev, ...vinculacion }));
            setNotification({ 
                show: true, 
                message: `¡Circuito completado! Póliza ${vinculacion.nContratoVinculado} emitida y anterior anulada correctamente.`, 
                type: 'success' 
            });
        } catch (e) {
            console.error("Error al finalizar vinculación de reemplazo de ART:", e);
        }
    }, [cotizacion, tempContractData, state]);

    const handleBackClick = useCallback(() => {
        if (onBack) onBack();
        else navigate(-1);
    }, [onBack, navigate]);

    return {
        state: {
            loading: combinedLoading,
            cotizacion,
            fetchedClient,
            endosoFull,
            contractFull,
            isGenerating,
            showContratoModal,
            showAnulacionModal,
            notification,
            contratoPrefill,
            clientDataForModal,
            selectedComparacion,
            tempContractData,
            isFullUser,
            captureRef
        },
        actions: {
            setLoading,
            setCotizacion,
            setFetchedClient,
            setEndosoFull,
            setContractFull,
            setIsGenerating,
            setShowContratoModal,
            setShowAnulacionModal,
            setNotification,
            setContratoPrefill,
            setClientDataForModal,
            setSelectedComparacion,
            setTempContractData,
            handleOpenContractModal,
            handleContractSuccess,
            handleAnulacionSuccess,
            handleBackClick,
            navigate
        }
    };
};